/**
 * Custom hook for API search functionality with fuzzy matching
 * 
 * @fileoverview Search hook using Fuse.js for fuzzy search
 */

import { useMemo } from 'react';
import Fuse from 'fuse.js';

/**
 * @typedef {Object} Filters
 * @property {string} [server] - Server type filter
 * @property {string} [category] - Category filter
 * @property {boolean} [onlyFavorites] - Only show favorites
 * @property {string} [method] - HTTP method filter
 */

/**
 * Fuse.js search options
 */
const fuseOptions = {
  keys: [
    { name: 'name', weight: 2 },
    { name: 'description', weight: 1 },
    { name: 'tags', weight: 1.5 },
    { name: 'category', weight: 1 },
    { name: 'endpoint', weight: 0.5 },
  ],
  threshold: 0.3,
  includeScore: true,
  minMatchCharLength: 2,
};

/**
 * Hook for API search functionality
 * @param {Object[]} apis - Array of API definitions
 * @param {string} query - Search query
 * @param {Filters} filters - Filter options
 * @returns {Object[]} Filtered and searched APIs
 */
export function useAPISearch(apis, query, filters = {}) {
  const fuse = useMemo(() => {
    if (!apis || apis.length === 0) return null;
    return new Fuse(apis, fuseOptions);
  }, [apis]);

  const results = useMemo(() => {
    if (!apis || apis.length === 0) return [];

    let filtered = [...apis];

    // Apply filters
    if (filters.server) {
      filtered = filtered.filter((api) => api.server === filters.server);
    }

    if (filters.category) {
      filtered = filtered.filter((api) => api.category === filters.category);
    }

    if (filters.onlyFavorites) {
      filtered = filtered.filter((api) => api.is_favorite === true);
    }

    if (filters.method) {
      filtered = filtered.filter((api) => api.method === filters.method);
    }

    // Apply search
    if (query && query.trim().length > 0 && fuse) {
      const searchResults = fuse.search(query);
      const searchedItems = searchResults.map((result) => result.item);
      
      // Intersect filtered and searched results
      const filteredIds = new Set(filtered.map((api) => api.id));
      return searchedItems.filter((item) => filteredIds.has(item.id));
    }

    return filtered;
  }, [apis, query, filters, fuse]);

  return results;
}

